// src/components/resources/tools/generators/AdMockup/AdMockup.tsx

/**
 * @fileoverview Ad Mockup / Ad Preview Generator
 * @description Renders realistic paid-ad previews across placements so
 * marketers, agencies, and media buyers can preview campaign concepts — and
 * export a deck-ready PNG before a single dollar is spent. Placements:
 *   - Facebook Feed & Instagram Feed (the original Meta link-ad chrome)
 *   - Instagram/Facebook Stories & Reels (full-screen vertical placement)
 *   - Google Responsive Search Ad (text-ad preview with live spec validation)
 *
 * Unlike the Social Media Post Mockup Generator (which renders ORGANIC posts),
 * this tool renders the PAID ad chrome: the "Sponsored" marker, the link card
 * with headline + description + display URL, the CTA button, the full-screen
 * Stories sticker CTA, and the Google search-result ad — the elements that
 * distinguish an ad preview from an organic post mockup.
 *
 * Accuracy moat: the Google Responsive Search Ad placement enforces Google's
 * real character limits live (headline ≤30, description ≤90, path ≤15) and the
 * Meta placements flag the ~125-char primary-text truncation point — surfacing
 * "this will get cut off" is genuinely useful pre-flight feedback that a
 * pixels-only mockup tool can't give. See {@link SPEC} and {@link CharCounter}.
 *
 * The component is preset-aware (`initialPlatform`) and accepts a `trackingSlug`
 * so future platform-qualified landing variants (facebook-ad-mockup-generator,
 * google-ad-preview-tool, …) can attribute their own engagement events,
 * mirroring the SocialMediaMockup variant pattern.
 *
 * Demo-trust: defaults are intentionally neutral placeholders (no verified
 * badge, generic brand/URL, template copy) so a default, unedited export can't
 * read as a live, endorsed ad for a real brand.
 *
 * The placement subcomponents (Facebook/Instagram/Story/TikTok/LinkedIn/
 * YouTube/GoogleSearch ads, plus Avatar / CreativeSlot / Field / CharCounter),
 * the pure helpers (ad-format.ts), and the shared types + ad-spec constants
 * (types.ts) live in sibling modules; this orchestrator owns the state, the
 * controls form, the deferred-preview boundary, and the export lifecycle.
 *
 * @module AdMockup
 */

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { toast } from 'sonner'
import { PhotoIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import {
  type AdMockupProps,
  type AdState,
  type Cta,
  type Theme,
  CAPS,
  CTA_OPTIONS,
  DEFAULT_STATE,
  MAX_UPLOAD_BYTES,
  PLACEMENTS,
  SPEC,
} from './types'
import { Field } from './Field'
import { CharCounter } from './CharCounter'
import { FacebookAd } from './FacebookAd'
import { InstagramAd } from './InstagramAd'
import { StoryAd } from './StoryAd'
import { TikTokAd } from './TikTokAd'
import { LinkedInAd } from './LinkedInAd'
import { YouTubeAd } from './YouTubeAd'
import { GoogleSearchAd } from './GoogleSearchAd'

export function AdMockup({
  initialPlatform = 'facebook',
  trackingSlug = 'ad-mockup-generator',
}: AdMockupProps): JSX.Element {
  const [state, setState] = useState<AdState>({ ...DEFAULT_STATE, platform: initialPlatform })
  const [creativeImage, setCreativeImage] = useState<string | null>(null)
  const [avatarImage, setAvatarImage] = useState<string | null>(null)
  // Shared export lifecycle: warms html2canvas on mount, a synchronous
  // re-entrancy guard, and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()

  // Render the 7 image-laden placement previews from a `useDeferredValue`
  // snapshot so copy keystrokes in the Controls column commit synchronously
  // (the inputs read live `state`) while the heavy preview tree re-renders at a
  // lower priority — the same boundary SocialMediaMockup/FakeChat/AiChat use to
  // shorten the long interaction task behind the generator pages' mobile INP.
  // Safe: the preview has no text inputs (only the export reads its DOM), and
  // `exportPng` waits for the snapshot to converge before capture, so resting
  // output and the exported PNG always match the live controls.
  const { deferred: deferredState, isStale: stateStale } = usePreviewDeferral(state)
  const { deferred: deferredCreativeImage, isStale: creativeStale } = usePreviewDeferral(creativeImage)
  const { deferred: deferredAvatarImage, isStale: avatarStale } = usePreviewDeferral(avatarImage)
  const previewStaleRef = useStaleRef(stateStale || creativeStale || avatarStale)

  const previewRef = useRef<HTMLDivElement>(null)
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  const update = useCallback(<K extends keyof AdState>(key: K, value: AdState[K]): void => {
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const updateGoogleHeadline = useCallback((index: number, value: string): void => {
    setState(prev => {
      const next = [...prev.gHeadlines] as AdState['gHeadlines']
      next[index] = value
      return { ...prev, gHeadlines: next }
    })
  }, [])

  const updateGoogleDescription = useCallback((index: number, value: string): void => {
    setState(prev => {
      const next = [...prev.gDescriptions] as AdState['gDescriptions']
      next[index] = value
      return { ...prev, gDescriptions: next }
    })
  }, [])

  const handleImage = useCallback((event: ChangeEvent<HTMLInputElement>, target: 'creative' | 'avatar'): void => {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
      input.value = ''
      return
    }
    const reader = new FileReader()
    reader.onerror = () => {
      toast.error('Could not read that image. Please try another file.')
      input.value = ''
    }
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : null
      if (target === 'creative') setCreativeImage(url)
      else setAvatarImage(url)
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: `upload_${target}`, file_type: file.type, file_size: file.size },
      })
    }
    reader.readAsDataURL(file)
  }, [getDeltaEngagementMs, trackingSlug])

  const reset = useCallback((): void => {
    setState({ ...DEFAULT_STATE, platform: state.platform })
    setCreativeImage(null)
    setAvatarImage(null)
  }, [state.platform])

  const exportPng = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) {
      toast.error('Preview not found. Please ensure the mockup is visible.')
      return
    }
    // The preview renders from `deferredState`; if the user edited copy or
    // switched placement and immediately hit Export, the deferred tree may
    // not have committed yet. Wait for it to converge so the PNG matches the
    // live controls (and the filename's platform); if it can't settle within
    // the ceiling, fail loudly rather than export a stale frame. We wait BEFORE
    // `beginCapture` so the disabled state tracks the actual capture, not this
    // (up-to-600ms) gate, and never shows on the abort path.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    // Capture starting: captureAndDownload yields a frame so this disabled
    // state paints before html2canvas blocks the main thread (Download INP).
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `${state.platform}-ad-mockup`,
      trackingSlug,
      engagementMs: getTotalEngagementMs(),
      extraParams: { platform: state.platform, cta: state.cta },
      onSuccess: () => toast.success('Ad mockup exported!'),
      onError: () => toast.error('Failed to export mockup. Please try again.'),
    })
  }), [runExport, getTotalEngagementMs, state.cta, state.platform, trackingSlug, previewStaleRef])

  const inputClasses =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'

  const isGoogle = state.platform === 'google-search'
  // Direct discriminant check (not `isGoogle`) so TS narrows `state.platform`
  // to the non-google union that indexes CAPS.
  const caps = state.platform === 'google-search' ? null : CAPS[state.platform]

  // Preview-side discriminants read the DEFERRED platform so the preview
  // wrapper's width class and footnote match the ad component actually being
  // rendered (which reads `deferredState`), not a placement switch that hasn't
  // committed to the low-priority preview tree yet.
  const previewIsGoogle = deferredState.platform === 'google-search'
  const previewCaps = deferredState.platform === 'google-search' ? null : CAPS[deferredState.platform]

  // Memoize the preview ELEMENT so the per-keystroke urgent re-render (deferred*
  // values referentially unchanged) reuses it and React bails out of re-rendering
  // the heavy ad chrome — it then renders only on the low-priority deferred pass.
  // Without this, useDeferredValue double-renders the preview and *raises* INP.
  const adPreview = useMemo(() => (
    <>
      {deferredState.platform === 'facebook' && <FacebookAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'instagram' && <InstagramAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'story' && <StoryAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'tiktok' && <TikTokAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'linkedin' && <LinkedInAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'youtube' && <YouTubeAd state={deferredState} creativeImage={deferredCreativeImage} avatarImage={deferredAvatarImage} />}
      {deferredState.platform === 'google-search' && <GoogleSearchAd state={deferredState} />}
    </>
  ), [deferredState, deferredCreativeImage, deferredAvatarImage])

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0 lg:flex-row lg:items-start">
      {/* ── Controls ─────────────────────────────────────────────── */}
      <section className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Design your ad preview</h2>

        {/* Placement toggle — segmented-control grid (even rows, no cramped
            wrap). 2 cols on mobile, 4 on desktop so 7 placements read as a
            tidy 4+3 rather than a ragged flex-wrap. */}
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-1.5 rounded-2xl bg-slate-100 p-1.5 sm:grid-cols-4 dark:bg-slate-800/60">
            {PLACEMENTS.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => update('platform', p.id)}
                aria-pressed={state.platform === p.id}
                className={clsx(
                  'rounded-xl px-3 py-2 text-center text-sm font-medium transition',
                  state.platform === p.id
                    ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {isGoogle ? (
          /* ── Google Responsive Search Ad controls (live spec validation) ── */
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Display URL (domain)" htmlFor="ad-displayUrl" hint="Google shows your final-URL domain — not a path you type.">
              <input id="ad-displayUrl" className={inputClasses} value={state.displayUrl} maxLength={60}
                placeholder="yourbrand.com" onChange={e => update('displayUrl', e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Path 1" htmlFor="ad-gpath1">
                <input id="ad-gpath1" className={inputClasses} value={state.gPath1} maxLength={SPEC.googlePath}
                  placeholder="collection" onChange={e => update('gPath1', e.target.value)} />
                <CharCounter value={state.gPath1} max={SPEC.googlePath} />
              </Field>
              <Field label="Path 2" htmlFor="ad-gpath2">
                <input id="ad-gpath2" className={inputClasses} value={state.gPath2} maxLength={SPEC.googlePath}
                  placeholder="new" onChange={e => update('gPath2', e.target.value)} />
                <CharCounter value={state.gPath2} max={SPEC.googlePath} />
              </Field>
            </div>
            {state.gHeadlines.map((h, i) => (
              <Field key={`gh-${i}`} label={`Headline ${i + 1}${i > 0 ? ' (optional)' : ''}`} htmlFor={`ad-gh-${i}`} full={i === 2}>
                <input id={`ad-gh-${i}`} className={inputClasses} value={h} maxLength={SPEC.googleHeadline}
                  onChange={e => updateGoogleHeadline(i, e.target.value)} />
                <CharCounter value={h} max={SPEC.googleHeadline} />
              </Field>
            ))}
            {state.gDescriptions.map((d, i) => (
              <Field key={`gd-${i}`} label={`Description ${i + 1}${i > 0 ? ' (optional)' : ''}`} htmlFor={`ad-gd-${i}`} full>
                <textarea id={`ad-gd-${i}`} className={clsx(inputClasses, 'min-h-[64px] resize-y')} value={d} maxLength={SPEC.googleDescription}
                  onChange={e => updateGoogleDescription(i, e.target.value)} />
                <CharCounter value={d} max={SPEC.googleDescription} />
              </Field>
            ))}
            <Field label="Theme" htmlFor="ad-theme" hint="Matches Google's light or dark search results.">
              <select id="ad-theme" className={inputClasses} value={state.theme}
                onChange={e => update('theme', e.target.value as Theme)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </Field>
          </div>
        ) : caps ? (
          /* ── Feed / vertical / video placement controls (config-driven) ── */
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label={state.platform === 'youtube' ? 'Channel / advertiser name' : 'Page / brand name'} htmlFor="ad-pageName">
              <input id="ad-pageName" className={inputClasses} value={state.pageName} maxLength={50}
                onChange={e => update('pageName', e.target.value)} />
            </Field>
            {caps.displayUrl && (
              <Field label="Display URL" htmlFor="ad-displayUrl" hint={state.platform === 'instagram' ? 'Shown on the Facebook placement only.' : undefined}>
                <input id="ad-displayUrl" className={inputClasses} value={state.displayUrl} maxLength={60}
                  placeholder="yourbrand.com" onChange={e => update('displayUrl', e.target.value)} />
              </Field>
            )}

            {caps.caption && (
              <Field
                label={caps.captionLabel}
                htmlFor="ad-primaryText"
                full
                hint={state.primaryText.length > SPEC.metaPrimaryTruncate ? `Feed truncates with “… See more” after ~${SPEC.metaPrimaryTruncate} characters (you're at ${state.primaryText.length}).` : undefined}
              >
                <textarea id="ad-primaryText" className={clsx(inputClasses, 'min-h-[88px] resize-y')} value={state.primaryText} maxLength={600}
                  onChange={e => update('primaryText', e.target.value)} />
              </Field>
            )}

            {caps.headline && (
              <Field label="Headline" htmlFor="ad-headline">
                <input id="ad-headline" className={inputClasses} value={state.headline} maxLength={60}
                  onChange={e => update('headline', e.target.value)} />
              </Field>
            )}
            {caps.description && (
              <Field label="Description" htmlFor="ad-description" hint={state.platform === 'instagram' ? 'Shown on the Facebook placement only.' : undefined}>
                <input id="ad-description" className={inputClasses} value={state.description} maxLength={90}
                  onChange={e => update('description', e.target.value)} />
              </Field>
            )}

            <Field label="Call to action" htmlFor="ad-cta">
              <select id="ad-cta" className={inputClasses} value={state.cta}
                onChange={e => update('cta', e.target.value as Cta)}>
                {CTA_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {caps.theme && (
              <Field label="Theme" htmlFor="ad-theme">
                <select id="ad-theme" className={inputClasses} value={state.theme}
                  onChange={e => update('theme', e.target.value as Theme)}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </Field>
            )}

            <Field label={caps.creativeLabel}>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-300">
                <PhotoIcon className="h-5 w-5" />
                {creativeImage ? 'Replace image' : 'Upload image'}
                <input type="file" accept="image/*" aria-label="Upload ad creative image" className="sr-only"
                  onChange={e => handleImage(e, 'creative')} />
              </label>
            </Field>
            <Field label="Profile avatar (optional)">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-300">
                <PhotoIcon className="h-5 w-5" />
                {avatarImage ? 'Replace avatar' : 'Upload avatar'}
                <input type="file" accept="image/*" aria-label="Upload profile avatar image" className="sr-only"
                  onChange={e => handleImage(e, 'avatar')} />
              </label>
            </Field>

            {caps.engagement && (
              <>
                <Field label="Likes" htmlFor="ad-likes">
                  <input id="ad-likes" type="number" min={0} className={inputClasses} value={state.likes}
                    onChange={e => update('likes', Math.max(0, Number(e.target.value) || 0))} />
                </Field>
                <Field label="Comments" htmlFor="ad-comments">
                  <input id="ad-comments" type="number" min={0} className={inputClasses} value={state.comments}
                    onChange={e => update('comments', Math.max(0, Number(e.target.value) || 0))} />
                </Field>
                {caps.shares && (
                  <Field label="Shares" htmlFor="ad-shares">
                    <input id="ad-shares" type="number" min={0} className={inputClasses} value={state.shares}
                      onChange={e => update('shares', Math.max(0, Number(e.target.value) || 0))} />
                  </Field>
                )}
              </>
            )}
            <Field label="Verified badge">
              <label className="flex h-[42px] items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={state.verified}
                  onChange={e => update('verified', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600" />
                Show verified check
              </label>
            </Field>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-5 dark:border-slate-700/70">
          <ExportButton
            isExporting={isExporting}
            onExport={() => void exportPng()}
            className="rounded-full px-6 py-2 text-sm font-semibold text-white shadow-sm transition"
            idleClassName="bg-indigo-600 hover:bg-indigo-700"
            exportingClassName="cursor-not-allowed bg-indigo-400"
          />
          <button type="button" onClick={reset}
            className="flex items-center gap-1.5 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:text-red-600 dark:border-slate-600 dark:text-slate-200">
            <ArrowPathIcon className="h-4 w-4" /> Reset
          </button>
        </div>
      </section>

      {/* ── Preview ──────────────────────────────────────────────── */}
      <aside className="lg:sticky lg:top-24 lg:w-[26rem]">
        <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-slate-400">Live preview</p>
        <div className="flex justify-center">
          <div ref={previewRef} className={clsx('w-full', previewCaps?.vertical ? 'max-w-[300px]' : 'max-w-[420px]')}>
            {adPreview}
          </div>
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">
          {previewIsGoogle
            ? 'For concept reviews and approvals — Google rotates headlines/descriptions live. Build real ads in Google Ads.'
            : 'For client decks and concept reviews — not a substitute for a live ad. Build the real ad in the platform’s Ads Manager.'}
        </p>
      </aside>
    </div>
  )
}
